import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import browserHistoryEntries from '~/services/BrowserHistoryEntries';
import navigationHistory from '~/services/NavigationHistory';
import { userStore } from '~/stores';
import { bottomNavigationStore } from '~/stores/BottomNavigationStore';

export const useBottomNavigation = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    bottomNavigationStore.reset();

    if (bottomNavigationStore.filters) {
      bottomNavigationStore.setState('withActions');
      return;
    }
    if (pathname.startsWith('/p/')) {
      bottomNavigationStore.setState('product');
      return;
    }

    if (pathname === '/cart') {
      bottomNavigationStore.setState('cart');
      return;
    }

    if (pathname === '/checkout') {
      bottomNavigationStore.setState('checkout');
      return;
    }

    if (pathname === '/search') {
      bottomNavigationStore.setState('default');
      return;
    }

    if (
      company.is(CompanyName.DubaiExpress) &&
      pathname.startsWith('/order/')
    ) {
      bottomNavigationStore.setState('withBack');
      return;
    }

    const routesWithBack = [
      '/c/',
      '/cabinet/',
      '/orders',
      '/address',
      '/payment',
      '/settings',
      '/notifications',
      '/help',
      '/favs',
    ];

    if (routesWithBack.some((route) => pathname.startsWith(route))) {
      bottomNavigationStore.setState('withBack');
      return;
    }

    // Routes that need back button
    const routesBackOnly = [
      '/about',
      '/terms',
      '/privacy',
      '/contact-us',
      '/legals',
      '/our-story',
      '/order/',
    ];

    if (routesBackOnly.some((route) => pathname.startsWith(route))) {
      bottomNavigationStore.setState('backOnly');
      return;
    }

    // Default state for all other routes
    bottomNavigationStore.setState('default');
  }, [pathname, bottomNavigationStore.filters]);

  return bottomNavigationStore;
};

export const useBottomNavigationBackHandler = () => {
  const navigate = useNavigate();

  return useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const previousPath = navigationHistory.goBack();

    if (previousPath) {
      if (
        previousPath === '/checkout' &&
        userStore.personalData.kycStatus !== KYCStatus.Verified
      ) {
        navigate('/cabinet');
        return;
      }

      // Unwinding history is what the reader means by "back", and it is the
      // only form of it that has a scroll position to come back to. The stacks
      // agree for as long as both were built in this page load, so take the
      // real pop whenever they still point at the same page — and fall back to
      // navigating when they do not, which is the cold start, where there is
      // nothing behind us in the browser to pop to anyway.
      const previousPathname = previousPath.split('?')[0] ?? previousPath;

      if (browserHistoryEntries.isPreviousEntry(previousPathname)) {
        navigate(-1);
        return;
      }

      // Not a pop, so nothing about this navigation says "return" on its own —
      // the destination sees a plain push and would reset the state the reader
      // is coming back to. Saying so in the location state is what stops it.
      navigate(previousPath, { state: { isBack: true } });
      return;
    }

    navigate('/');
  }, [navigate]);
};
